import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { DownloadSimple, Clock, Lock, Coins } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

export default function PendingAdjustments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get("/reports/pending-cost-adjustments").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  const rows = data?.rows || [];
  const cols = [
    { key: "lx_po_no", label: "Somcore PO" }, { key: "client_name", label: "Client" },
    { key: "supplier_name", label: "Supplier" },
    { key: "blocked_periods", label: "Locked Period(s)", format: (v) => (v || []).join("; ") },
    { key: "variance", label: "Cost Variance", format: fmtNum },
    { key: "created_at", label: "Flagged" }, { key: "note", label: "Note" },
  ];

  return (
    <Layout>
      <PageHeader
        title="Pending Cost Adjustments"
        subtitle="POs whose real supplier cost couldn't be posted to inventory because an affected period was locked. Clear each as a proper prior-period adjustment (unlock & re-match, or a manual journal)."
        actions={<Button variant="outline" data-testid="export-pending-adj" onClick={() => exportToCsv("pending-cost-adjustments", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>}
      />
      <div className="p-8">
        {err ? (
          <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="pending-adj-error">Could not load. Please refresh or check you have executive access.</div>
        ) : !data ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <KpiStrip cols={2}>
              <KpiCard icon={Lock} label="POs Awaiting Adjustment" value={data.summary.count} tone={data.summary.count ? "danger" : "info"} testid="kpi-pending-count" />
              <KpiCard icon={Coins} label="Net Cost Variance (TZS)" value={fmtNum(data.summary.total_variance, "TZS")} tone="accent" testid="kpi-pending-variance" />
            </KpiStrip>

            {rows.length === 0 ? (
              <EmptyState icon={Clock} testid="pending-adj-empty" title="Nothing pending" message="Every supplier-cost reconciliation posted cleanly — no locked-period adjustments are waiting." />
            ) : (
              <div className="space-y-3">
                {rows.map((r) => (
                  <div key={r.transaction_id} data-testid={`pending-adj-${r.transaction_id}`} className="rounded-sm border border-amber-200 bg-amber-50/50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono-num font-semibold">{r.lx_po_no || "—"}</span>
                        <span className="ml-2 text-sm text-gray-600">{r.client_name || "—"}{r.supplier_name ? ` · ${r.supplier_name}` : ""}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-500">Flagged {r.created_at || "—"}</span>
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Locked: {(r.blocked_periods || []).join(", ")}</span>
                        <span className="lx-num font-semibold text-red-600">{curSym(r.currency)} {fmtNum(r.variance)}</span>
                        <button className="text-xs font-semibold text-[#1B4383] hover:underline" data-testid={`pending-adj-open-${r.transaction_id}`} onClick={() => navigate(`/transactions/${r.transaction_id}`)}>Open PO</button>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1 text-xs">
                      {(r.variances || []).map((v, j) => (
                        <div key={j} className="lx-num flex justify-between gap-4 text-gray-600">
                          <span className="truncate">{v.description || v.sku || `Line ${v.line_index}`}</span>
                          <span className="whitespace-nowrap">est {fmtNum(v.estimate_unit_tzs)} → act {v.actual_unit_tzs == null ? "—" : fmtNum(v.actual_unit_tzs)} ({fmtNum(v.variance_tzs)})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs text-gray-400">To clear: ask Ronick to unlock the period (the reconciliation then auto-rebuilds AVCO/COGS), or post a manual prior-period cost adjustment.</p>
          </>
        )}
      </div>
    </Layout>
  );
}
